// ============================================================================
// AXIOS INTERCEPTORS
// ============================================================================
// Two sets of interceptors:
// - App client: Bearer token, tenant headers, proactive + reactive refresh
// - Auth client: Only tenant context headers (no Bearer token)

import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig, HTTP_STATUS } from '../config/apiConfig';
import { tokenManager } from '../../auth/tokenManager';
import { STORAGE_KEYS } from '../../auth/storageKeys';
import { logRequest, logResponse, logError, logWarning } from './logger';

const TOKEN_REFRESH_BUFFER_MS = 60_000; // 60 seconds before expiry

// ============================================================================
// APP CLIENT INTERCEPTORS (authenticated business endpoints)
// ============================================================================

export const setupAppInterceptors = (axiosInstance: AxiosInstance): void => {
  // === Request Interceptor ===
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      config.headers['X-Request-Start-Time'] = Date.now().toString();

      // Proactive token refresh: if token expires within 60s, refresh BEFORE sending.
      // Guard `> 0` — an already-expired token is handled reactively by the 401 handler,
      // which knows how to redirect on total failure. Attempting a proactive refresh here
      // just to fail again would double the network trip on the way to the same redirect.
      const token = tokenManager.getAccessToken();
      if (token) {
        const timeUntilExpiry = tokenManager.getTimeUntilExpiry(token);
        if (timeUntilExpiry !== null && timeUntilExpiry < TOKEN_REFRESH_BUFFER_MS && timeUntilExpiry > 0) {
          try {
            await proactiveRefresh(axiosInstance);
          } catch {
            // If proactive refresh fails, continue with the current token.
            // The response interceptor will handle 401 reactively.
          }
        }
        // Attach token (possibly refreshed)
        const currentToken = tokenManager.getAccessToken();
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
      }

      // Attach tenant context headers.
      // Resolve scope selections with a persisted-permissions fallback. AuthContext
      // writes SELECTED_*_ID to localStorage on login and again on mount-time recovery,
      // but there are edge cases where those keys are missing while
      // `permission_context.scopeConfiguration` is not (fresh tab across a bundle-version
      // wipe, HMR that skipped the context provider, first-login race). Reading the
      // primary entity/BU/FY straight out of the persisted context guarantees the very
      // next request carries the right headers without waiting for re-login.
      const tenantId = tokenManager.getTenantId();
      const appId = tokenManager.getAppId();
      const userId = tokenManager.getUserId();

      const readScopeFallback = () => {
        try {
          const raw = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
          if (!raw) return { entity: null, bu: null, fy: null };
          const scope = JSON.parse(raw)?.scopeConfiguration;
          if (!scope) return { entity: null, bu: null, fy: null };
          const e = scope.accessibleEntities?.find((x: any) => x.isPrimary)
                 ?? scope.accessibleEntities?.[0];
          const b = scope.accessibleBusinessUnits?.find((x: any) => x.isPrimary)
                 ?? scope.accessibleBusinessUnits?.[0];
          const f = scope.accessibleFinancialYears?.find((x: any) => x.isPrimary)
                 ?? scope.accessibleFinancialYears?.find((x: any) => x.isCurrent)
                 ?? scope.accessibleFinancialYears?.[0];
          return { entity: e?.id ?? null, bu: b?.id ?? null, fy: f?.id ?? null };
        } catch {
          return { entity: null, bu: null, fy: null };
        }
      };
      const fallback = readScopeFallback();

      const entityId = localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY_ID) || fallback.entity;
      const buId = localStorage.getItem(STORAGE_KEYS.SELECTED_BU_ID) || fallback.bu;
      const fyId = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_ID) || fallback.fy;

      if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
      if (appId) config.headers['X-App-ID'] = appId;
      if (userId) config.headers['X-User-ID'] = userId;
      if (fyId) config.headers['X-Financial-Year-ID'] = fyId;
      if (entityId) config.headers['X-Entity-ID'] = entityId;
      if (buId) config.headers['X-Business-Unit-ID'] = buId;

      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      config.headers['Accept'] = 'application/json';

      logRequest(config);
      return config;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );

  // === Response Interceptor (reactive 401 handling) ===
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      logResponse(response);
      return response;
    },
    async (error: AxiosError) => {
      logError(error);

      if (error.response) {
        const { status } = error.response;

        if (status === HTTP_STATUS.UNAUTHORIZED) {
          return handleUnauthorizedError(axiosInstance, error);
        }

        if (status === HTTP_STATUS.FORBIDDEN) {
          logWarning('Access forbidden - Insufficient permissions');
        }

        if (status === HTTP_STATUS.NOT_FOUND) {
          logWarning('Resource not found', error.config?.url);
        }

        if (status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          logWarning('Internal server error');
        }
      } else if (error.request) {
        logWarning('Network error - No response from server');
      } else {
        logWarning('Unknown error occurred', error.message);
      }

      return Promise.reject(error);
    }
  );
};

// ============================================================================
// AUTH CLIENT INTERCEPTORS (discover, authenticate, refresh — no Bearer token)
// ============================================================================

export const setupAuthInterceptors = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers['X-Request-Start-Time'] = Date.now().toString();

      // Only attach tenant context (no Bearer token for auth endpoints)
      const tenantId = tokenManager.getTenantId();
      const appId = tokenManager.getAppId();

      if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
      if (appId) config.headers['X-App-ID'] = appId;

      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      config.headers['Accept'] = 'application/json';

      logRequest(config);
      return config;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      logResponse(response);
      return response;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );
};

// ============================================================================
// REACTIVE TOKEN REFRESH (401 handler with request queue)
// ============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

async function handleUnauthorizedError(
  axiosInstance: AxiosInstance,
  error: AxiosError
): Promise<any> {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (originalRequest._retry) {
    logWarning('Token refresh failed; clearing session and redirecting to login.');
    tokenManager.clearAll();
    // .replace() overwrites the current history entry so Back cannot re-enter
    // an authenticated view — matches the logout redirect pattern.
    window.location.replace('/');
    return Promise.reject(error);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      })
      .catch(err => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const refreshToken = tokenManager.getRefreshToken();

  if (!refreshToken) {
    logWarning('No refresh token available; clearing session and redirecting to login.');
    tokenManager.clearAll();
    window.location.replace('/');
    return Promise.reject(error);
  }

  try {
    const newToken = await performTokenRefresh(axiosInstance);
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    processQueue(null, newToken);
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    logWarning('Token refresh API failed; clearing session and redirecting to login.');
    tokenManager.clearAll();
    window.location.replace('/');
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

// ============================================================================
// PROACTIVE TOKEN REFRESH
// ============================================================================

let isProactiveRefreshing = false;

async function proactiveRefresh(axiosInstance: AxiosInstance): Promise<void> {
  if (isProactiveRefreshing || isRefreshing) return;
  isProactiveRefreshing = true;
  try {
    await performTokenRefresh(axiosInstance);
  } finally {
    isProactiveRefreshing = false;
  }
}

// ============================================================================
// SHARED REFRESH LOGIC
// ============================================================================

async function performTokenRefresh(_axiosInstance: AxiosInstance): Promise<string> {
  // Use a separate axios call to avoid interceptor loops
  const { default: axios } = await import('axios');

  // CRS /auth/refresh proxies to TenantHub /api/v1/Auth/refresh.
  // TenantHub expects the refresh token in the body (not the access token in a header).
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const tenantId = tokenManager.getTenantId();
  const appId = tokenManager.getAppId();

  // Send tenant/app context both in body (TenantHub contract) and headers (CRS proxy fallback)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  if (appId) headers['X-App-ID'] = appId;

  const response = await axios.post(
    `${apiConfig.baseURL}/${apiConfig.apiVersion}/auth/refresh`,
    {
      refreshToken,
      tenantId: tenantId || undefined,
      applicationId: appId || undefined,
    },
    {
      headers,
      timeout: apiConfig.timeout,
    }
  );

  const data = response.data;
  // TenantHub response shape: { success, data: { accessToken, refreshToken, ... } }
  // Also tolerate a direct AuthenticationResponse for safety
  const tokenData = data?.data ?? data;
  const newAccessToken = tokenData?.accessToken ?? tokenData?.token;
  const newRefreshToken = tokenData?.refreshToken;

  if (!newAccessToken) {
    throw new Error('Refresh response missing access token');
  }

  tokenManager.setAccessToken(newAccessToken);
  if (newRefreshToken) {
    tokenManager.setRefreshToken(newRefreshToken);
  }

  return newAccessToken;
}
